import axios from "axios";
import { useFormik } from "formik";
import { enqueueSnackbar } from "notistack";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { API } from "../../../helpers/constants";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../../../helpers/errorHandling";
import useUserAction from "../../../hooks/user/useUserAction";
import useUserData from "../../../hooks/user/useUserData";
import { ISSUES_FORM } from "../locales/namespaces";
import * as yup from "yup";

const useEmailSO = ({onEmailSendSuccess = () => {}}) => {
	const { t } = useTranslation(ISSUES_FORM)
	const { token, username, userId, isLoggedIn, dateFormat, timeFormat } = useUserData()
	const { userLogout } = useUserAction()
	const [loadingSubmit, setLoadingSubmit] = useState(false)
	const [open, setOpen] = useState(false)
	
	const sendOffer = useCallback((values) => {
		setLoadingSubmit(true)
		axios({
			method: 'post',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			data: {
				id: values.id,
				to: values.to.filter(i => i.value?.trim())?.map(i => (i.value))?.join(';'),
				cc: values.cc.filter(i => i.value?.trim())?.map(i => (i.value))?.join(';'),
				bcc: values.bcc.filter(i => i.value?.trim())?.map(i => (i.value))?.join(';'),
			},
			url: `${API}/en/custom/workOrder/send/email`
		}).then(response => {
			enqueueSnackbar(t(`Customer offer sent successfully!`), {
				variant: 'success',
				autoHideDuration: 3000,
			})
			setLoadingSubmit(false)
			setOpen(false)
			onEmailSendSuccess({values, response})
		}).catch((error) => {
			setLoadingSubmit(false)
			const errorMessage = `${t('An error occurred!')} ${error?.response?.data?.errors}`;
			enqueueSnackbar(handleAxiosErrors(error, errorMessage), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [t, isLoggedIn, userLogout, onEmailSendSuccess])
	
	const emailIfPresent = yup
	.string()
	.trim()
	.transform(v => (v === "" ? null : v))
	.nullable()
	.email(t("Invalid email")); // only runs when not null
	
	const validationSchema = yup.object().shape({
		to: yup
		.array()
		.of(
			yup.object({
				id: yup.mixed().required(),
				value: emailIfPresent, // per-field: if filled, must be valid
			})
		)
		// At least one field must be filled (doesn't force which one)
		.test("at-least-one", function (arr) {
			const hasAny = Array.isArray(arr) && arr.some(it => !!(it?.value && String(it.value).trim()));
			return hasAny || this.createError({
				path: `${this.path}[0].value`, // show near the first input
				message: t("Required"),
			});
		}),
		cc: yup
		.array()
		.of(
			yup.object({
				id: yup.mixed().required(),
				value: emailIfPresent, // per-field: if filled, must be valid
			})
		),
		bcc: yup
		.array()
		.of(
			yup.object({
				id: yup.mixed().required(),
				value: emailIfPresent, // per-field: if filled, must be valid
			})
		)
	})
	
	const formik = useFormik({
		initialValues: {
			id: '',
			to: [],
			cc: [],
			bcc: [],
			body: '',
			subject: '',
		},
		validationSchema: validationSchema,
		onSubmit: sendOffer,
	})
	
	return {
		emailFormik: formik,
		loadingSendEmail: loadingSubmit,
		open,
		setOpen
	}
}

export default useEmailSO